/* i18n dictionaries for AZEX (EN primary, AR secondary). */
import { nameCodes } from "@/lib/wsNames";
import { I18N_LAYERS } from "@/lib/i18n/index.generated";   // STEP 2 · B2: every translation file in lib/i18n/, in prefix order
export const translations = {
  en: {
    app: {
      name: "Azex Audit",
      tagline: "AI-assisted financial audit workspace",
    },
    itemSignoff: {
      level: { preparer: "Preparer", reviewer1: "Reviewer 1", reviewer2: "Reviewer 2", reviewer3: "Reviewer 3", eqc: "EQ reviewer", partner: "Partner" },
      sign: "Sign", remove: "Remove", editDate: "Change the date",
      workDate: "Date the work was done (optional)", clickedAt: "signed on",   // Batch 14 · M5
      changedAfter: "Changed after sign-off", signedEmpty: "Signed but empty",
      emptyWarning: "This item is empty. The sign-off is recorded and the item shows 'Signed but empty'.",
      failed: "The sign-off could not be saved.", locked: "The file is locked — sign-offs cannot change.",
      samplingSheet: "Sampling sheet", vouchingSheet: "Vouching sheet",
      tabTitle: "Sign-offs", table: "Table", timeline: "Timeline", item: "Item", folder: "Folder", flags: "Flags",
      filterLevel: "Level", filterFolder: "Folder", allLevels: "All levels", allFolders: "All folders",
      onlyMissing: "Missing only", onlyFlagged: "Flagged only", exportCsv: "Export CSV", exportXlsx: "Export Excel",
      na: "N/A", naNotRecorded: "reason not recorded",   // Batch 14 · M11
      noItems: "No items in this engagement yet.", noEvents: "No sign-off yet.",
      ev: { signed: "signed", date_changed: "changed the date", removed: "removed", level_removed: "level removed" },
      rollup: "Sign-offs", rollupLine: "{label} {signed} / {total}",
      reviewLevels: "Review levels", reviewLevelsHelp: "Reviewer 2 and Reviewer 3 can be added or removed at any stage. The EQ reviewer level shows when the engagement requires an EQ review.",
      engagementPartner: "Engagement partner (signs the Partner level)", anyPartner: "Any Partner of the firm",
      confirmRemoveLevel: "Remove the level? Its sign-offs leave the bar and the table and stay in the timeline.",
      save: "Save", saved: "Review levels saved",
      lockMissingTitle: "The file cannot be locked — Preparer or Reviewer 1 missing",
      lockWarningsTitle: "Warnings (the lock goes ahead)", lockGoAhead: "Lock the file",
    },
    nav: {
      dashboard: "Dashboard",
      executive: "Executive",
      engagements: "Engagements",
      glImport: "GL Import",
      analytics: "Analytics",
      sampling: "Sampling",
      vouching: "Vouching Control / Summary",
      docs: "Document Review",
      docSummaries: "Document Summaries",
      discrepancies: "Discrepancy Report",
      settings: "Settings",
      ifrs: "IFRS Statements",
      assistant: "AI Audit Assistant",
      auditTrail: "Audit Trail",
      risk: "Risk Assessment",
      ratios: "Financial Ratios",
      aging: "Aging Analysis",
      platform: "Tenants",
      comingSoon: "Coming soon",
    },
    layoutNav: {
      groups: {
        engagement_setup: "Engagement Setup",
        acceptance: "Acceptance & Continuance",
        planning: "Planning",
        planning_risk: "Planning & Risk",
        risk_assessment: "Risk Assessment",
        digital_audit: "Digital Audit",
        analytics: "Analytics & Insights",
        audit_response: "Audit Response",
        controls: "Controls",
        fieldwork: "Fieldwork",
        completion: "Completion",
        reporting: "Reporting",
        reporting_completion: "Reporting & Completion",
        close_archive: "Close & Archive",
        governance: "Governance & Quality",
        workspace: "Workspace",
      },
      items: {
        acceptance: "Acceptance & Continuance",
        planning: "Planning",
        scoping: "Scoping",
        risk_assessment_forms: "Risk Assessment Forms",
        risk_a9001_pointer: "Fraud red flags (ISA 240)",
        correlation: "Correlation",
        sub_ledger_receivable: "Sub GL Analyzer — Sales/Receivable",
        sub_ledger_payable: "Sub GL Analyzer — Trade Payable",
        sub_ledger_inventory: "Sub GL Analyzer — Inventory",
        audit_response: "Audit Response Plan",
        controls_203a: "Control Design (203(a))",   // Batch 22 · Add-on 1
        controls_walkthroughs: "Walkthroughs + Design & Implementation",
        controls_itgc: "IT General Controls",
        controls_a9002: "Tests of Controls",
        controls_deficiency_log: "Control Deficiency Log",
        confirmations: "Confirmations",
        head_folders: "Head Folders",
        fieldwork_a9001: "Journal Entry Testing and Fraud Red Flags (ISA 240)",
        fieldwork_a9003: "Opening Balances",
        fieldwork_adjusting_entries: "Adjusting entries",
        misstatements: "Misstatements Summary",
        misstatement_worksheet: "Misstatement worksheet",
        completion: "Completion",
        review_points: "Review Points",
        audit_summary_memo: "Audit Summary Memo",
        review_summary_memo: "Review Summary Memo",
        methodology: "Methodology",
        disclosure_checklist: "Disclosure Checklist",
        eq_review_link: "EQ Review Link",
        clearance_gate: "Clearance Gate",
        kam_register: "KAM Register",
        other_information: "Other Information",
        firm_report_formats: "Firm Report Formats",
        auditors_report: "Auditor's Report",
        file_assembly: "File Assembly",
        recycle_bin: "Recycle Bin",
        specialized: "Specialized & Other Forms",
        doc_library: "Document Library",
        isqm: "ISQM (Quality Management)",
        verify_bundle: "Verify Signed Bundle",
        billing: "Billing / Over Run",
        ai_usage: "AI Usage",
        help: "Help Center",
      },
      platform: {
        tenants: "Tenants",
        users: "Users",
        plans: "Plans",
        plan_requests: "Plan requests",
        ai_keys: "AI Keys",
        worker_rollout: "Worker Rollout",
        worker_diagnostics: "Worker Diagnostics",
        storage_health: "Storage Health",
        errors: "Errors",
        saved_copies: "Saved copies",
        recycle_bin: "Recycle Bin",
        maintenance: "Maintenance",
        package_keys: "Package keys",   // Batch 14 · M15
        email_provider: "Email Provider",
        diagnostics: "Diagnostics",
      },
      chrome: {
        navigation: "Navigation",
        expand_all: "Expand all",
        collapse_all: "Collapse all",
        activity_log: "Activity Log",
        group_min_pct_tooltip: "Lowest completion inside this group",
        switch_to_top: "Switch to top navigation",
        collapse_nav: "Collapse navigation",
        expand_nav: "Expand navigation",
        switch_to_side: "Switch to side navigation",
        view_logo: "View logo",
        brand_logo_alt: "Brand logo",
        azex_logo_alt: "Azex",
        close_aria: "Close",
      },
    },
    assistant: {
      lockedBanner: "Engagement is locked — chat is read-only",
      title: "AI Audit Assistant",
      subtitle: "Ask questions about this engagement — the assistant reads the GL and documents",
      emptyTitle: "Ask anything about this engagement",
      emptySub: "The assistant has access to the trial balance, analytics, discrepancies and extracted document fields",
      placeholder: "Ask a question about this engagement…",
      disclaimer: "AI-generated answers may contain errors — always verify against the source data.",
      clear: "Clear history",
    },
    auth: {
      login: "Log in",
      register: "Create account",
      email: "Email",
      password: "Password",
      name: "Full name",
      orgName: "Firm / Organization",
      country: "Country",
      noAccount: "Don't have an account?",
      haveAccount: "Already have an account?",
      demoCred: "",
      tagline: "Secure sign-in · Azex Audit",
      loginTitle: "Sign in to Azex Audit",
      lockedTitle: "Too many attempts",
      lockedAccount: "Too many attempts. This account is locked. Try again after {m} minutes.",
      lockedAddress: "Too many attempts from this address. Try again after {m} minutes.",
      attemptsLeft: "Incorrect password. Try again. {n} attempts left before the account is locked.",
      registerTitle: "Create your firm workspace on Azex Audit",
      // Batch 17 · F-139: the one generic reply of the password reset (the same whether the account exists or not)
      forgotTitle: "Forgot your password?", forgotDoneTitle: "Check your e-mail",
      forgotIntro: "Enter the e-mail of your account. If an account exists, a reset link is sent to it.",
      forgotGeneric: "If an account exists for this e-mail, a reset link has been sent.",
      forgotValid: "The link is valid for 2 hours.",
      sendNewCode: "Send a new code",
      sendNewCodeIn: "Send a new code in {s}s",
      sendNewCodeFailed: "Could not send a new code",
      codeNotDelivered: "We couldn't email your code (delivery failed). Tap \"Send a new code\" below or try again shortly.",
      forgotDevLink: "Local copy without an e-mail service: the reset link is shown here only on a dev / local copy.",
    },
    layer: { clientBooks: "Client books", adjusted: "After audit adjustments", hintClient: "Figures as uploaded by the client — adjusting entries are not applied here.", hintAdjusted: "Figures after the posted adjusting entries." },
    common: {
      ok: "OK",
      more: "More",   // Batch 19 · B6 (F-145)
      less: "Less",
      menu: "Menu",   // Batch 19 · C3: the phone drawer button
      pageNotFound: "Page not found",   // Batch 10 · B7 (F-79)
      carriedForward: "Carried forward — update for current year",   // Batch 10.1 · R2
      pageNotFoundHelp: "The page you opened does not exist or is not available to your account.",
      uploadFailed: "Upload failed — please try again",
      loading: "Loading…",
      chooseFile: "Choose file",
      chooseFiles: "Choose files",
      noFileChosen: "No file chosen",
      remove: "Remove",
      saving: "Saving…",
      failed: "Failed",
      save: "Save",
      cancel: "Cancel",
      create: "Create",
      export: "Export",
      run: "Run",
      total: "Total",
      amount: "Amount",
      date: "Date",
      description: "Description",
      account: "Account",
      debit: "Debit",
      credit: "Credit",
      status: "Status",
      actions: "Actions",
      language: "Language",
      logout: "Log out",
      profile: "Profile",
      openEng: "Open engagement",
      selectEng: "Select engagement",
      noEng: "No engagement selected",
      search: "Search",
      review: "AI-generated — review required",
      high: "High",
      medium: "Medium",
      low: "Low",
      yes: "Yes",
      no: "No",
      close: "Close",
      completed: "Completed",
      fullScreen: "Full screen",   // Batch 23 · Stage 6(b)
      fullScreenHint: "Hide the sidebar and the top bar so the page uses the whole window (Esc brings them back)",
      fullScreenOnHint: "Full screen — the sidebar and the top bar are hidden. Esc or Exit brings them back.",
      exitFullScreen: "Exit full screen",
      browserFullScreen: "Browser full screen",
      rows: "rows",
      remove: "Remove",
      noFileChosen: "No file chosen",
      viewSent: "View entry",
      costEstimateNotAvailable: "Cost estimate not available",
      forItemsSuffix: "for {label}",
      aiUnits: "AI units",
      confirm: "Confirm",
      view: "View",
      download: "Download",
      upload: "Upload",
      failed: "Failed",
      supportingDocs: "Supporting documents",
      name: "Name",
      role: "Role",
      week: "week",
      month: "month",
      previousSlide: "Previous slide",
      nextSlide: "Next slide",
      addWorksheet: "Add worksheet",
      notEntered: "Not entered",
      incomplete: "Incomplete",
      openLink: "Open",
      done: "Done",
      empty: "Empty",
      pending: "Pending",
      yes: "Yes",
      no: "No",
    },
    placeholder: {
      badgeTitle: "Available in a later build",
      body: "This section's content will be built in a later batch. It counts as N/A for completion percentages until then.",
      formsHeader: "Forms assigned here",
      formsHint: "These forms have been placed under this section by the engagement's type profile. Click any row to open the form in its underlying tab.",
      openForm: "Open form",
    },
    signoff: { header: "Sequential sign-off: Preparer → Reviewer → EQ reviewer (form 522(a) completed and signed, when required) → Partner. The Partner sign-off dates the report, generates the tamper-evident bundle and freezes the engagement (ISQM 2 / ISA 220 (Revised))." },
    pages: {
      acceptance: { title: "Acceptance", purpose: "Client acceptance & continuance, independence, and the engagement letter." },
      audit_response: { title: "Audit Response", purpose: "The Audit Response Plan and its derived detailed audit plan / risk-addressed forms." },
      scoping: { title: "Scoping", purpose: "Determine which FS heads are in-scope for substantive procedures." },
      controls_203a: { title: "Control Design (203(a))", purpose: "203(a) Control Design — Financial Statement Areas worksheet: the design of controls per financial statement area, the source of the walkthrough processes." },
      controls_walkthroughs: { title: "Walkthroughs + Design & Implementation", purpose: "Walkthrough documentation for each significant control." },
      controls_itgc: { title: "IT General Controls", purpose: "General IT controls that support the reliability of the financial reporting systems." },
      controls_a9002: { title: "Tests of Controls", purpose: "Operating-effectiveness testing of controls the audit intends to rely on." },
      controls_deficiency_log: { title: "Deficiency Log", purpose: "Central log of control deficiencies feeding form 322(a) — Management Letter." },
      head_folders: { title: "Head Folders", purpose: "One folder per FS head from the FS mapping, with the standard file structure (-01 to -06+)." },
      fieldwork_a9001: { title: "Journal Entry Testing and Fraud Red Flags (ISA 240)", purpose: "Selection criteria, ISA 240 fingerprints population, samples and conclusion." },
      fieldwork_a9003: { title: "Opening Balances", purpose: "PY closing vs CY opening reconciliation, procedures and conclusion (holds 213(a))." },
      misstatements: { title: "Misstatements", purpose: "Summary of misstatements feeding forms 300–302(a). Only Unresolved is evaluated against materiality." },
      audit_summary_memo: { title: "Audit Summary Memorandum", purpose: "Consolidated engagement summary supporting the audit opinion." },
      eq_review_link: { title: "EQ Review 522(a)", purpose: "Engagement Quality Review linked from Completion — opens the existing ISQM 2 form." },
      clearance_gate: { title: "Clearance Gate", purpose: "Final pre-issuance clearance checklist before the audit report is signed." },
      auditors_report: { title: "Auditor's Report", purpose: "Auditor's report generator — ISA 700/701/705/706 wording tailored to the engagement." },
      file_assembly: { title: "File Assembly", purpose: "Final audit file assembly. After assembly, every change requires a stated reason and is logged; nothing can be deleted." },
    },
    attachments: {
      label: "Supporting documents",
      signedOffToast: "Engagement is signed off — unlock to attach documents.",
      deleteConfirm: "Delete",
      deleted: "Attachment deleted",
      downloadFailed: "Download failed",
      readOnly: "Engagement is signed off — attachments are read-only.",
      formats: "PDF · Images (PNG/JPG/WEBP) · CSV · XLS/XLSX · max 25 MB each",
      download: "Download",
      delete: "Delete",
    },
    presence: {
      live: "Live",
      engagement: "engagement",
      onlyOne: "You're the only one here",
      viewing: "viewing",
      empty: "No one else is currently viewing this engagement.",
      tenantNote: "Presence stays within your firm — no one outside this tenant is shown.",
      online: "online",
    },
    workflow: {
      title: "Workflow",
      history: "Workflow history",
      noTransitions: "No transitions yet.",
      sendBackTitle: "Send form back to draft",
      sendBackDesc: "The preparer will see your reason.",
      sendBackPlaceholder: "What needs to be fixed?",
      sendBack: "Send back",
      sendForReview: "Send for review",
      markReady: "Mark ready",
      approve: "Approve",
      reopen: "Reopen",
      actionFailed: "Action failed",
      reasonTooShort: "Please write a short reason (min 3 characters).",
      statusChanged: "Form status → {status}",
      draft: "Draft",
      ready: "Ready for review",
      approved: "Approved",
    },
    dash: {
      title: "Dashboard",
      subtitle: "Overview of all engagements and audit findings",
      totalEng: "Total Engagements",
      inProgress: "In Progress",
      totalGL: "GL Entries",
      highRisk: "High-Risk Findings",
      recent: "Recent engagements",
      viewAll: "View all",
      findings: "Findings by risk",
    },
    b3: {
      hwCsv: "Head-wise CSV", hwXlsx: "Head-wise XLSX", controlPopulationPh: "control population", txTypeAccepts: "Transaction Type accepts:",
      description: "Description", item: "Item", location: "Location", qty: "Qty", unitCost: "Unit cost", value: "Value", urlRefPh: "URL / ref",
      glImport: "GL Import", allFindings: "All findings", fsMapping: "FS Mapping", pickEngagement: "Select an engagement first.",
      bv: { title: "Signed Bundle Verifier", scope: "Scope of this check:", upload: "Upload bundle", snapshot: "Engagement snapshot", firm: "Firm", client: "Client",
            engRef: "Engagement Ref", fy: "Financial Year", periodEnd: "Period End", currency: "Currency", signedBy: "Signed off by", signedAt: "Signed off at",
            fingerprint: "Contents fingerprint", samples: "Samples", vouching: "Vouching", documents: "Documents", discrepancies: "Discrepancies", riskItems: "Risk items",
            attachments: "Attachments", files: "Files inside the archive" },
    },
    // Slice 5 — countable-noun plurals routed through fmtPlural(count, lang, dict.plurals.<key>).
    // English cardinal categories: one / other. Missing categories collapse to other per Slice 7.
    plurals: {
      docs:          { one: "{n} doc",          other: "{n} docs" },
      groups:        { one: "{n} group",        other: "{n} groups" },
      glEntries:     { one: "{n} entry",        other: "{n} entries" },
      logEntries:    { one: "{n} entry",        other: "{n} entries" },
      items:         { one: "{n} item",         other: "{n} items" },
      forms:         { one: "{n} form",         other: "{n} forms" },
      links:         { one: "{n} link",         other: "{n} links" },
      addons:        { one: "{n} add-on",       other: "{n} add-ons" },
      tenants:       { one: "{n} tenant",       other: "{n} tenants" },
      rules:         { one: "{n} rule",         other: "{n} rules" },
      accounts:      { one: "{n} account",      other: "{n} accounts" },
      suggestions:   { one: "{n} suggestion",   other: "{n} suggestions" },
      confirmations: { one: "{n} confirmation", other: "{n} confirmations" },
      exports:       { one: "{n} export",       other: "{n} exports" },
      rows:          { one: "{n} row",          other: "{n} rows" },
      others:        { one: "{n} other",        other: "{n} others" },
      responses:     { one: "{n} response",     other: "{n} responses" },
      invitations:   { one: "{n} invitation",   other: "{n} invitations" },
      sheets:        { one: "{n} sheet",        other: "{n} sheets" },
      sampleLines:   { one: "{n} sample line",  other: "{n} sample lines" },
      reviewPoints:  { one: "{n} review point", other: "{n} review points" },
      rates:         { one: "{n} rate",         other: "{n} rates" },
      matches:       { one: "{n} match",        other: "{n} matches" },
      misstatements: { one: "{n} misstatement", other: "{n} misstatements" },
      transactions:  { one: "{n} transaction",  other: "{n} transactions" },
      tieBreaks:     { one: "{n} tie break",    other: "{n} tie breaks" },
      freshSheets:   { one: "{n} fresh sheet",  other: "{n} fresh sheets" },
      // Slice 5 Fork 1 — new nouns added Feb 22, 2026. Anchors verified
      // against shipped errors/COA copy (see review-sheet evidence in
      // conversation log): file→ملف (errors.ISQM_UPLOAD_EMPTY),
      // mapping→تعيين (errors.COA_BULK_MAPPING_IDS_EMPTY), code→رمز
      // (errors.HEADWISE_UNKNOWN_FS_CODE), key→مفتاح
      // (errors.AI_KEY_NOT_FOUND).
      files:         { one: "{n} file",         other: "{n} files" },
      mappings:      { one: "{n} mapping",      other: "{n} mappings" },
      codes:         { one: "{n} code",         other: "{n} codes" },
      keys:          { one: "{n} key",          other: "{n} keys" },
      // placementGroups added Feb 22, 2026. Anchor verified via shipped
      // error FS_CUSTOM_PLACEMENT_INVALID at i18n.js:1566 ("الموضع
      // (Placement) ليس من القيم المسموح بها.") + VALID_PLACEMENTS in
      // backend/custom_codes.py (SFP sections ∪ P&L). Sense: which
      // section of the F/S a line lives in.
      placementGroups: { one: "{n} placement group", other: "{n} placement groups" },
      // Compound plurals — modifier stays INSIDE the plural entry so
      // Arabic word order (noun-then-adjective, agreement) is preserved.
      // A bare-noun + external-modifier approach reads wrong in Arabic
      // ("valid صفوف" — modifier before noun; correct is "صفوف صالحة").
      // Anchors: FS→transliterated (matches shipped nav.fsMapping);
      // note→ملاحظة (shipped errors.NOTE_*); AI→الذكاء الاصطناعي (shipped
      // tagline + errors.AI_KEY_ISSUE_*); add-on→إضافي (shipped
      // errors.AUTH_STEP_UP_REQUIRED); valid→صالح/صالحة (standard MSA);
      // head-wise→حسب البند (audit-jargon judgement, flagged for
      // Saudi-auditor confirmation); ISQM→transliterated (shipped
      // isqm.title, nav.isqm).
      fsMappings:      { one: "{n} FS mapping",      other: "{n} FS mappings" },
      noteMappings:    { one: "{n} note mapping",    other: "{n} note mappings" },
      aiSuggestions:   { one: "{n} AI suggestion",   other: "{n} AI suggestions" },
      addonForms:      { one: "{n} add-on form",     other: "{n} add-on forms" },
      validRows:       { one: "{n} valid row",       other: "{n} valid rows" },
      headwiseRows:    { one: "{n} head-wise row",   other: "{n} head-wise rows" },
      isqmInvitations: { one: "{n} ISQM invitation", other: "{n} ISQM invitations" },
    },
  },
  ar: {

    app: {
      name: "Azex Audit",
      tagline: "منصة تدقيق مالي مدعومة بالذكاء الاصطناعي",
    },
    itemSignoff: {
      level: { preparer: "المُعِد", reviewer1: "المراجع 1", reviewer2: "المراجع 2", reviewer3: "المراجع 3", eqc: "مراجع جودة الارتباط", partner: "الشريك" },
      sign: "توقيع", remove: "إزالة", editDate: "تغيير التاريخ",
      workDate: "تاريخ إنجاز العمل (اختياري)", clickedAt: "وُقّع في",   // Batch 14 · M5
      changedAfter: "تغيّر بعد التوقيع", signedEmpty: "موقَّع لكنه فارغ",
      emptyWarning: "هذا البند فارغ. تم تسجيل التوقيع ويظهر البند بعبارة «موقَّع لكنه فارغ».",
      failed: "تعذّر حفظ التوقيع.", locked: "الملف مقفل — لا يمكن تغيير التوقيعات.",
      samplingSheet: "ورقة العينة", vouchingSheet: "ورقة التحقق المستندي",
      tabTitle: "التوقيعات", table: "جدول", timeline: "تسلسل زمني", item: "البند", folder: "المجلد", flags: "تنبيهات",
      filterLevel: "المستوى", filterFolder: "المجلد", allLevels: "كل المستويات", allFolders: "كل المجلدات",
      onlyMissing: "الناقص فقط", onlyFlagged: "المنبَّه فقط", exportCsv: "تصدير CSV", exportXlsx: "تصدير Excel",
      na: "غير قابل للتطبيق", naNotRecorded: "لم يُسجَّل سبب",   // Batch 14 · M11
      noItems: "لا توجد بنود في هذا الارتباط بعد.", noEvents: "لا توجد توقيعات بعد.",
      ev: { signed: "وقّع", date_changed: "غيّر التاريخ", removed: "أزال", level_removed: "أُزيل المستوى" },
      rollup: "التوقيعات", rollupLine: "{label} {signed} / {total}",
      reviewLevels: "مستويات المراجعة", reviewLevelsHelp: "يمكن إضافة المراجع 2 والمراجع 3 أو إزالتهما في أي مرحلة. يظهر مستوى مراجع جودة الارتباط عندما يتطلب الارتباط مراجعة جودة.",
      engagementPartner: "شريك الارتباط (يوقّع مستوى الشريك)", anyPartner: "أي شريك في المكتب",
      confirmRemoveLevel: "إزالة المستوى؟ تُحذف توقيعاته من الشريط والجدول وتبقى في التسلسل الزمني.",
      save: "حفظ", saved: "تم حفظ مستويات المراجعة",
      lockMissingTitle: "لا يمكن قفل الملف — توقيع المُعِد أو المراجع 1 ناقص",
      lockWarningsTitle: "تنبيهات (يستمر القفل)", lockGoAhead: "قفل الملف",
    },
    nav: {
      dashboard: "لوحة التحكم",
      executive: "لوحة الشركاء",
      engagements: "المهام",
      glImport: "استيراد الأستاذ العام",
      analytics: "التحليلات",
      sampling: "العينات",
      vouching: "مطابقة السندات / الملخص",
      docs: "مراجعة المستندات",
      docSummaries: "ملخصات المستندات",
      discrepancies: "تقرير الفروقات",
      settings: "الإعدادات",
      ifrs: "قوائم IFRS",
      assistant: "مساعد التدقيق الذكي",
      auditTrail: "سجل التدقيق",
      risk: "تقييم المخاطر",
      ratios: "النسب المالية",
      aging: "تحليل الأعمار",
      platform: "المستأجرون",
      comingSoon: "قريباً",
    },
    layoutNav: {
      groups: {
        engagement_setup: "إعداد المهمة",
        acceptance: "القبول والاستمرارية",
        planning: "التخطيط",
        planning_risk: "التخطيط والمخاطر",
        risk_assessment: "تقييم المخاطر",
        digital_audit: "المراجعة الرقمية",
        analytics: "التحليلات والرؤى",
        audit_response: "استجابة المراجعة",
        controls: "الضوابط",
        fieldwork: "العمل الميداني",
        completion: "الإكمال",
        reporting: "التقرير",
        reporting_completion: "التقرير والإكمال",
        close_archive: "الإغلاق والأرشفة",
        governance: "الحوكمة والجودة",
        workspace: "مساحة العمل",
      },
      items: {
        acceptance: "القبول والاستمرارية",
        planning: "التخطيط",
        scoping: "تحديد النطاق",
        risk_assessment_forms: "نماذج تقييم المخاطر",
        risk_a9001_pointer: "مؤشرات الاحتيال \u2066(ISA 240)\u2069",
        correlation: "الارتباط",
        sub_ledger_receivable: "محلل الأستاذ الفرعي — المبيعات/المدينون",
        sub_ledger_payable: "محلل الأستاذ الفرعي — الدائنون التجاريون",
        sub_ledger_inventory: "محلل الأستاذ الفرعي — المخزون",
        audit_response: "خطة الاستجابة للمراجعة",
        controls_203a: "تصميم الرقابة (203(a))",   // Batch 22 · Add-on 1
        controls_walkthroughs: "اختبار التتبع \u2066+\u2069 التصميم والتطبيق",
        controls_itgc: "الضوابط العامة لتقنية المعلومات",
        controls_a9002: "اختبارات الضوابط",
        controls_deficiency_log: "سجل قصور الضوابط",
        confirmations: "التأكيدات",
        head_folders: "مجلدات البنود",
      fieldwork_a9001: "اختبار قيود اليومية ومؤشرات الاحتيال \u2066(ISA 240)\u2069",
        fieldwork_a9003: "الأرصدة الافتتاحية",
        fieldwork_adjusting_entries: "قيود التسوية",
        misstatements: "ملخص التحريفات",
        misstatement_worksheet: "ورقة تقييم التحريفات",
        completion: "الإكمال",
        review_points: "نقاط المراجعة",
        audit_summary_memo: "مذكرة ملخص المراجعة",
        review_summary_memo: "مذكرة ملخص الفحص",
        methodology: "منهجية المراجعة",
        disclosure_checklist: "قائمة الإفصاح",
        eq_review_link: "رابط مراجعة الجودة",
        clearance_gate: "بوابة التصفية",
        kam_register: "سجل أمور المراجعة الرئيسية",
        other_information: "المعلومات الأخرى",
        firm_report_formats: "نماذج تقارير الشركة",
        auditors_report: "تقرير المراجع",
        file_assembly: "تجميع الملف",
        recycle_bin: "سلة المحذوفات",
        specialized: "نماذج متخصصة وأخرى",
        doc_library: "مكتبة المستندات",
        isqm: "ISQM (إدارة الجودة)",
        verify_bundle: "التحقق من الحزمة الموقّعة",
        billing: "الفوترة / تجاوز الميزانية",
        ai_usage: "استخدام الذكاء الاصطناعي",
        help: "مركز المساعدة",
      },
      platform: {
        tenants: "المستأجرون",
        users: "المستخدمون",
        plans: "الخطط",
        plan_requests: "طلبات الخطط",
        ai_keys: "مفاتيح الذكاء الاصطناعي",
        worker_rollout: "طرح العمال",
        worker_diagnostics: "تشخيصات العمال",
        storage_health: "صحة التخزين",
        errors: "الأخطاء",
        saved_copies: "النسخ المحفوظة",
        recycle_bin: "سلة المحذوفات",
        maintenance: "الصيانة",
        package_keys: "مفاتيح الحزم",   // Batch 14 · M15
        email_provider: "مزوّد البريد",
        diagnostics: "التشخيصات",
      },
      chrome: {
        navigation: "التنقّل",
        expand_all: "توسيع الكل",
        collapse_all: "طيّ الكل",
        activity_log: "سجل النشاط",
        group_min_pct_tooltip: "أدنى نسبة إكمال داخل هذه المجموعة",
        switch_to_top: "التبديل إلى شريط التنقّل العلوي",
        collapse_nav: "طيّ شريط التنقّل",
        expand_nav: "توسيع شريط التنقّل",
        switch_to_side: "التبديل إلى الشريط الجانبي",
        view_logo: "عرض الشعار",
        brand_logo_alt: "شعار العلامة",
        azex_logo_alt: "Azex",
        close_aria: "إغلاق",
      },
    },
    assistant: {
      lockedBanner: "المهمة مقفلة — المحادثة للقراءة فقط",
      title: "مساعد التدقيق الذكي",
      subtitle: "اطرح أسئلة عن هذه المهمة — يقرأ المساعد الأستاذ والمستندات",
      emptyTitle: "اسأل أي شيء عن هذه المهمة",
      emptySub: "لدى المساعد اطلاع على ميزان المراجعة والتحليلات والفروقات والحقول المستخرجة",
      placeholder: "اطرح سؤالاً…",
      disclaimer: "قد تحتوي إجابات الذكاء الاصطناعي على أخطاء — تحقق دائماً من البيانات المصدرية.",
      clear: "مسح السجل",
    },
    auth: {
      forgotTitle: "هل نسيت كلمة المرور؟", forgotDoneTitle: "تحقّق من بريدك الإلكتروني",
      forgotIntro: "أدخل البريد الإلكتروني لحسابك. إذا كان هناك حساب، يُرسل إليه رابط إعادة التعيين.",
      forgotGeneric: "إذا كان هناك حساب لهذا البريد الإلكتروني، فقد أُرسل رابط إعادة التعيين.",
      forgotValid: "الرابط صالح لمدة ساعتين.",
      sendNewCode: "إرسال رمز جديد",
      sendNewCodeIn: "إرسال رمز جديد خلال {s} ث",
      sendNewCodeFailed: "تعذّر إرسال رمز جديد",
      codeNotDelivered: "تعذّر إرسال الرمز بالبريد الإلكتروني (فشل التسليم). اضغط «إرسال رمز جديد» أدناه أو حاول مرة أخرى بعد قليل.",
      forgotDevLink: "نسخة محلية بدون خدمة بريد: يظهر رابط إعادة التعيين هنا فقط على نسخة تطوير / محلية.",
      login: "تسجيل الدخول",
      register: "إنشاء حساب",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      name: "الاسم الكامل",
      orgName: "الشركة / المؤسسة",
      country: "الدولة",
      noAccount: "ليس لديك حساب؟",
      haveAccount: "لديك حساب بالفعل؟",
      demoCred: "",
      tagline: "تسجيل دخول آمن · Azex Audit",
      loginTitle: "تسجيل الدخول إلى Azex Audit",
      lockedTitle: "محاولات كثيرة جدًا",
      lockedAccount: "محاولات كثيرة جدًا. تم قفل هذا الحساب. حاول مرة أخرى بعد {m} دقيقة.",
      lockedAddress: "محاولات كثيرة جدًا من هذا العنوان. حاول مرة أخرى بعد {m} دقيقة.",
      attemptsLeft: "كلمة المرور غير صحيحة. حاول مرة أخرى. بقيت {n} محاولات قبل قفل الحساب.",
      registerTitle: "أنشئ مساحة عمل شركتك على Azex Audit",
    },
    layer: { clientBooks: "دفاتر العميل", adjusted: "بعد تسويات المراجعة", hintClient: "الأرقام كما رفعها العميل — قيود التسوية لا تُطبّق هنا.", hintAdjusted: "الأرقام بعد قيود التسوية المرحّلة." },
    common: {
      ok: "موافق",
      more: "المزيد",   // Batch 19 · B6 (F-145)
      less: "أقل",
      menu: "القائمة",   // Batch 19 · C3
      pageNotFound: "الصفحة غير موجودة",   // Batch 10 · B7 (F-79)
      carriedForward: "مرحَّل — حدِّث للسنة الحالية",   // Batch 10.1 · R2
      pageNotFoundHelp: "الصفحة التي فتحتها غير موجودة أو غير متاحة لحسابك.",
      uploadFailed: "فشل الرفع — يرجى المحاولة مرة أخرى",
      loading: "جاري التحميل…",
      chooseFile: "اختر ملفاً",
      chooseFiles: "اختر ملفات",
      noFileChosen: "لم يُختر ملف",
      remove: "إزالة",
      saving: "جاري الحفظ…",
      failed: "فشل",
      save: "حفظ",
      cancel: "إلغاء",
      create: "إنشاء",
      export: "تصدير",
      run: "تشغيل",
      total: "الإجمالي",
      amount: "المبلغ",
      date: "التاريخ",
      description: "الوصف",
      account: "الحساب",
      debit: "مدين",
      credit: "دائن",
      status: "الحالة",
      actions: "إجراءات",
      language: "اللغة",
      logout: "تسجيل الخروج",
      profile: "الملف الشخصي",
      openEng: "فتح المهمة",
      selectEng: "اختر مهمة",
      noEng: "لا توجد مهمة محددة",
      search: "بحث",
      review: "تم بواسطة الذكاء الاصطناعي — يتطلب مراجعة",
      high: "عالي",
      medium: "متوسط",
      low: "منخفض",
      yes: "نعم",
      no: "لا",
      close: "إغلاق",
      completed: "مكتمل",
      fullScreen: "ملء الشاشة",
      fullScreenHint: "إخفاء الشريط الجانبي والشريط العلوي لتستخدم الصفحة النافذة كلها (يعيدهما مفتاح Esc)",
      fullScreenOnHint: "ملء الشاشة — الشريط الجانبي والشريط العلوي مخفيان. يعيدهما مفتاح Esc أو زر الخروج.",
      exitFullScreen: "الخروج من ملء الشاشة",
      browserFullScreen: "ملء شاشة المتصفح",
      rows: "صفوف",
      remove: "حذف",
      noFileChosen: "لم يتم اختيار ملف",
      viewSent: "عرض القيد",
      costEstimateNotAvailable: "تقدير التكلفة غير متاح",
      forItemsSuffix: "لـ {label}",
      aiUnits: "وحدات ذكاء اصطناعي",
      confirm: "تأكيد",
      view: "عرض",
      download: "تنزيل",
      upload: "رفع",
      failed: "فشل",
      supportingDocs: "المستندات الداعمة",
      name: "الاسم",
      role: "الدور",
      week: "أسبوع",
      month: "شهر",
      previousSlide: "الشريحة السابقة",
      nextSlide: "الشريحة التالية",
      addWorksheet: "إضافة ورقة عمل",
      notEntered: "لم يُدخَل",
      incomplete: "غير مكتمل",
      openLink: "فتح",
      done: "مُنجز",
      empty: "فارغ",
      pending: "قيد الإنجاز",
      yes: "نعم",
      no: "لا",
    },
    placeholder: {
      badgeTitle: "متاح في إصدار لاحق",
      body: "سيتم بناء محتوى هذا القسم في دفعة لاحقة. يُعد غير منطبق لأغراض نسب الإكمال حتى ذلك الحين.",
      formsHeader: "النماذج المخصصة هنا",
      formsHint: "تم تخصيص هذه النماذج لهذا القسم من قِبل ملف نوع الارتباط. انقر على أي صف لفتح النموذج في تبويبه الأصلي.",
      openForm: "فتح النموذج",
    },
    signoff: { header: "التوقيع المتسلسل: المعد → المراجع → مراجع جودة الارتباط (استكمال نموذج 522(أ) وتوقيعه، عند اللزوم) → الشريك. توقيع الشريك يؤرّخ التقرير وينشئ الحزمة المقاومة للعبث ويجمّد الارتباط (ISQM 2 / ISA 220 المنقّح). توقيع الشريك يجمد الارتباط وينشئ الحزمة المقاومة للتلاعب." },
    pages: {
      acceptance: { title: "القبول والاستمرار", purpose: "قبول العميل والاستمرار، الاستقلال، وخطاب الارتباط." },
      audit_response: { title: "الاستجابة لمخاطر المراجعة", purpose: "خطة الاستجابة للمراجعة والنماذج المشتقة منها." },
      scoping: { title: "تحديد النطاق", purpose: "تحديد بنود القوائم المالية المشمولة بالإجراءات الجوهرية." },
      controls_203a: { title: "تصميم الرقابة (203(a))", purpose: "ورقة عمل 203(a) تصميم الرقابة — بنود القوائم المالية: تصميم الرقابة لكل بند من القوائم المالية، وهي مصدر عمليات اختبار التتبع." },
      controls_walkthroughs: { title: "الاستعراضات وتصميم وتنفيذ الرقابة", purpose: "توثيق استعراض كل عنصر رقابة جوهري." },
      controls_itgc: { title: "الرقابة العامة لتقنية المعلومات", purpose: "الرقابة العامة لتقنية المعلومات الداعمة لموثوقية أنظمة التقارير المالية." },
      controls_a9002: { title: "اختبارات أدوات الرقابة", purpose: "اختبار الفعالية التشغيلية لأدوات الرقابة التي ينوي المراجع الاعتماد عليها." },
      controls_deficiency_log: { title: "سجل أوجه القصور", purpose: "سجل مركزي لأوجه قصور الرقابة يغذي النموذج 322(a) — خطاب الإدارة." },
      head_folders: { title: "مجلدات البنود", purpose: "مجلد واحد لكل بند مالي من تعيين القوائم، بهيكل الملفات القياسي (-01 إلى -06+)." },
      fieldwork_a9001: { title: "اختبار قيود اليومية ومؤشرات الاحتيال \u2066(ISA 240)\u2069", purpose: "معايير الاختيار، مجتمع بصمات \u2066ISA 240\u2069، العينات والاستنتاج." },
      fieldwork_a9003: { title: "الأرصدة الافتتاحية", purpose: "مطابقة إقفال السنة السابقة مع افتتاح السنة الحالية، الإجراءات والاستنتاج (يحتفظ بـ 213(a))." },
      misstatements: { title: "التحريفات", purpose: "ملخص التحريفات يغذي النماذج 300–302(a). يتم تقييم غير المُصحّح فقط مقابل الأهمية النسبية." },
      audit_summary_memo: { title: "مذكرة ملخص المراجعة", purpose: "ملخص موحّد للارتباط يدعم رأي المراجع." },
      eq_review_link: { title: "مراجعة الجودة 522(a)", purpose: "مراجعة جودة الارتباط المرتبطة من قسم الإتمام — تفتح نموذج ISQM 2 الحالي." },
      clearance_gate: { title: "بوابة الإخلاء", purpose: "قائمة الإخلاء النهائية قبل الإصدار قبل توقيع تقرير المراجع." },
      auditors_report: { title: "تقرير المراجع", purpose: "منشئ تقرير المراجع — نصوص ISA 700/701/705/706 مخصصة للارتباط." },
      file_assembly: { title: "تجميع الملف", purpose: "التجميع النهائي لملف المراجعة. بعد التجميع، يتطلب كل تغيير سببًا معلنًا ويُسجَّل؛ لا يمكن حذف أي شيء." },
    },
    attachments: {
      label: "المستندات الداعمة",
      signedOffToast: "المهمة موقعة — قم بفك القفل لإرفاق المستندات.",
      deleteConfirm: "حذف",
      deleted: "تم حذف المرفق",
      downloadFailed: "فشل التنزيل",
      readOnly: "المهمة موقعة — المرفقات للقراءة فقط.",
      formats: "PDF · صور (PNG/JPG/WEBP) · CSV · XLS/XLSX · حد أقصى 25 ميجابايت لكل ملف",
      download: "تنزيل",
      delete: "حذف",
    },
    presence: {
      live: "مباشر",
      engagement: "المهمة",
      onlyOne: "أنت الوحيد هنا",
      viewing: "يشاهد",
      empty: "لا يوجد أحد آخر يشاهد هذه المهمة حالياً.",
      tenantNote: "الحضور يبقى داخل شركتك — لا يتم عرض أي شخص خارج هذه الجهة.",
      online: "متصل",
    },
    workflow: {
      title: "سير العمل",
      history: "سجل سير العمل",
      noTransitions: "لا توجد تحولات بعد.",
      sendBackTitle: "إعادة النموذج إلى المسودة",
      sendBackDesc: "سيرى المُعدّ سببك.",
      sendBackPlaceholder: "ما الذي يحتاج إلى تصحيح؟",
      sendBack: "إعادة",
      sendForReview: "إرسال للمراجعة",
      markReady: "تحديد كجاهز",
      approve: "اعتماد",
      reopen: "إعادة فتح",
      actionFailed: "فشل الإجراء",
      reasonTooShort: "يرجى كتابة سبب قصير (3 أحرف على الأقل).",
      statusChanged: "حالة النموذج ← {status}",
      draft: "مسودة",
      ready: "جاهز للمراجعة",
      approved: "معتمد",
    },
    dash: {
      title: "لوحة التحكم",
      subtitle: "نظرة عامة على جميع المهام والنتائج",
      totalEng: "إجمالي المهام",
      inProgress: "قيد التنفيذ",
      totalGL: "قيود دفتر الأستاذ",
      highRisk: "نتائج عالية المخاطر",
      recent: "المهام الحديثة",
      viewAll: "عرض الكل",
      findings: "النتائج حسب الخطر",
    },
    b3: {
      hwCsv: "CSV حسب البند", hwXlsx: "XLSX حسب البند", controlPopulationPh: "مجتمع الرقابة", txTypeAccepts: "نوع المعاملة يقبل:",
      description: "الوصف", item: "الصنف", location: "الموقع", qty: "الكمية", unitCost: "التكلفة للوحدة", value: "القيمة", urlRefPh: "رابط / مرجع",
      glImport: "استيراد الأستاذ العام", allFindings: "جميع النتائج", fsMapping: "ربط القوائم المالية", pickEngagement: "اختر المهمة أولًا.",
      bv: { title: "التحقق من الحزمة الموقّعة", scope: "نطاق هذا الفحص:", upload: "رفع الحزمة", snapshot: "لقطة الارتباط", firm: "الشركة", client: "العميل",
            engRef: "مرجع الارتباط", fy: "السنة المالية", periodEnd: "نهاية الفترة", currency: "العملة", signedBy: "وقّع عليه", signedAt: "وقت التوقيع",
            fingerprint: "بصمة المحتويات", samples: "العينات", vouching: "التوثيق", documents: "المستندات", discrepancies: "الاختلافات", riskItems: "بنود المخاطر",
            attachments: "المرفقات", files: "الملفات داخل الأرشيف" },
    },
    // Slice 5 — countable-noun plurals for fmtPlural (Intl.PluralRules ar-EG).
    // Reviewer-approved forms (paired-model review Feb 22, 2026). ISA vocabulary:
    // "misstatements" = تحريفات (SOCPA ISA 450), not أخطاء. "confirmations" = مصادقات
    // (ISA 505), not تأكيدات — anchored to Slice-4 CONFIRMATION_NOT_FOUND. Diacritics
    // (tanwin) dropped throughout for UI consistency, per reviewer.
    plurals: {
      docs:          { zero: "لا مستندات",         one: "مستند",              two: "مستندان",           few: "{n} مستندات",           other: "{n} مستند" },
      groups:        { zero: "لا مجموعات",         one: "مجموعة",              two: "مجموعتان",           few: "{n} مجموعات",           other: "{n} مجموعة" },
      glEntries:     { zero: "لا قيود",            one: "قيد واحد",            two: "قيدان",              few: "{n} قيود",              other: "{n} قيد" },
      logEntries:    { zero: "لا سجلات",           one: "سجل واحد",            two: "سجلان",              few: "{n} سجلات",             other: "{n} سجل" },
      items:         { zero: "لا عناصر",           one: "عنصر واحد",           two: "عنصران",             few: "{n} عناصر",             other: "{n} عنصر" },
      forms:         { zero: "لا نماذج",           one: "نموذج واحد",          two: "نموذجان",            few: "{n} نماذج",             other: "{n} نموذج" },
      links:         { zero: "لا روابط",           one: "رابط واحد",           two: "رابطان",             few: "{n} روابط",             other: "{n} رابط" },
      addons:        { zero: "لا إضافات",          one: "إضافة واحدة",         two: "إضافتان",            few: "{n} إضافات",            other: "{n} إضافة" },
      tenants:       { zero: "لا مستأجرين",        one: "مستأجر واحد",         two: "مستأجران",           few: "{n} مستأجرين",          other: "{n} مستأجر" },
      rules:         { zero: "لا قواعد",           one: "قاعدة واحدة",         two: "قاعدتان",            few: "{n} قواعد",             other: "{n} قاعدة" },
      accounts:      { zero: "لا حسابات",          one: "حساب واحد",           two: "حسابان",             few: "{n} حسابات",            other: "{n} حساب" },
      suggestions:   { zero: "لا اقتراحات",        one: "اقتراح واحد",         two: "اقتراحان",           few: "{n} اقتراحات",          other: "{n} اقتراح" },
      confirmations: { zero: "لا مصادقات",         one: "مصادقة واحدة",        two: "مصادقتان",           few: "{n} مصادقات",           other: "{n} مصادقة" },
      exports:       { zero: "لا عمليات تصدير",    one: "عملية تصدير واحدة",   two: "عمليتا تصدير",       few: "{n} عمليات تصدير",     other: "{n} عملية تصدير" },
      rows:          { zero: "لا صفوف",            one: "صف واحد",             two: "صفان",               few: "{n} صفوف",              other: "{n} صف" },
      others:        { zero: "لا مستخدمين آخرين",  one: "مستخدم آخر",          two: "مستخدمان آخران",     few: "{n} مستخدمين آخرين",    other: "{n} مستخدمين آخرين" },
      responses:     { zero: "لا استجابات",        one: "استجابة واحدة",       two: "استجابتان",          few: "{n} استجابات",          other: "{n} استجابة" },
      invitations:   { zero: "لا دعوات",           one: "دعوة واحدة",          two: "دعوتان",             few: "{n} دعوات",             other: "{n} دعوة" },
      sheets:        { zero: "لا أوراق",           one: "ورقة واحدة",          two: "ورقتان",             few: "{n} أوراق",             other: "{n} ورقة" },
      sampleLines:   { zero: "لا صفوف عينات",      one: "صف عينة",             two: "صفا عينة",           few: "{n} صفوف عينات",       other: "{n} صف عينة" },
      reviewPoints:  { zero: "لا نقاط مراجعة",     one: "نقطة مراجعة واحدة",   two: "نقطتا مراجعة",       few: "{n} نقاط مراجعة",      other: "{n} نقطة مراجعة" },
      rates:         { zero: "لا معدلات",          one: "معدل واحد",           two: "معدلان",             few: "{n} معدلات",            other: "{n} معدل" },
      matches:       { zero: "لا تطابقات",         one: "تطابق واحد",          two: "تطابقان",            few: "{n} تطابقات",           other: "{n} تطابق" },
      misstatements: { zero: "لا تحريفات جوهرية",  one: "تحريف جوهري واحد",    two: "تحريفان جوهريان",    few: "{n} تحريفات جوهرية",   other: "{n} تحريف جوهري" },
      transactions:  { zero: "لا معاملات",         one: "معاملة واحدة",         two: "معاملتان",           few: "{n} معاملات",          other: "{n} معاملة" },
      tieBreaks:     { zero: "لا فروق ربط",        one: "فرق ربط واحد",        two: "فرقا ربط",           few: "{n} فروق ربط",         other: "{n} فرق ربط" },
      freshSheets:   { zero: "لا أوراق جديدة",     one: "ورقة جديدة واحدة",    two: "ورقتان جديدتان",     few: "{n} أوراق جديدة",     other: "{n} ورقة جديدة" },
      // Slice 5 Fork 1 — 4 nouns added Feb 22, 2026. Anchors follow shipped errors/COA copy.
      files:         { zero: "لا ملفات",           one: "ملف واحد",             two: "ملفان",              few: "{n} ملفات",             other: "{n} ملف" },
      mappings:      { zero: "لا تعيينات",         one: "تعيين واحد",           two: "تعيينان",            few: "{n} تعيينات",           other: "{n} تعيين" },
      codes:         { zero: "لا رموز",            one: "رمز واحد",             two: "رمزان",              few: "{n} رموز",              other: "{n} رمز" },
      keys:          { zero: "لا مفاتيح",          one: "مفتاح واحد",           two: "مفتاحان",            few: "{n} مفاتيح",            other: "{n} مفتاح" },
      placementGroups: { zero: "لا مواضع",          one: "موضع واحد",            two: "موضعان",             few: "{n} مواضع",             other: "{n} موضع" },
      fsMappings:      { zero: "لا تعيينات FS",      one: "تعيين FS واحد",        two: "تعيينا FS",          few: "{n} تعيينات FS",        other: "{n} تعيين FS" },
      noteMappings:    { zero: "لا تعيينات ملاحظات", one: "تعيين ملاحظة واحد",   two: "تعيينا ملاحظات",     few: "{n} تعيينات ملاحظات",   other: "{n} تعيين ملاحظة" },
      aiSuggestions:   { zero: "لا اقتراحات ذكاء اصطناعي", one: "اقتراح ذكاء اصطناعي واحد", two: "اقتراحا ذكاء اصطناعي", few: "{n} اقتراحات ذكاء اصطناعي", other: "{n} اقتراح ذكاء اصطناعي" },
      addonForms:      { zero: "لا نماذج إضافية",     one: "نموذج إضافي واحد",     two: "نموذجان إضافيان",    few: "{n} نماذج إضافية",      other: "{n} نموذج إضافي" },
      validRows:       { zero: "لا صفوف صالحة",      one: "صف صالح واحد",         two: "صفان صالحان",        few: "{n} صفوف صالحة",        other: "{n} صف صالح" },
      headwiseRows:    { zero: "لا صفوف حسب البند",  one: "صف واحد حسب البند",   two: "صفان حسب البند",     few: "{n} صفوف حسب البند",   other: "{n} صف حسب البند" },
      isqmInvitations: { zero: "لا دعوات ISQM",      one: "دعوة ISQM واحدة",     two: "دعوتا ISQM",         few: "{n} دعوات ISQM",       other: "{n} دعوة ISQM" },
    },
  },
};

function _overrideDeep(target, src) {
  for (const k of Object.keys(src)) {
    if (src[k] && typeof src[k] === "object" && !Array.isArray(src[k])) { if (!target[k] || typeof target[k] !== "object") target[k] = {}; _overrideDeep(target[k], src[k]); }
    else target[k] = src[k];
  }
}
// STEP 2 · B2 — the translation files of lib/i18n/ (listed by index.generated.js, written by frontend/scripts/gen_i18n_index.js)
// are applied here in file-name prefix order: base screen files (0001–0999) first, then the override layers (1000+); a later
// file wins. A new file needs no edit here — add it to the folder and re-run the generator.
for (const { dict } of I18N_LAYERS) {
  _overrideDeep(translations.en, dict.en);
  _overrideDeep(translations.ar, dict.ar);
}

export function t(dict, path) {
  // Slice-1 fallback: walk the requested language dict for `path`; if that
  // resolves to a string, return it. Otherwise walk the EN dict for the
  // same path and return that. Raw dot-paths ("nav.executive") never
  // reach the UI unless BOTH dicts are missing the key.
  //
  // Comparison in the CI drift check (backend/tests/test_i18n_parity.py)
  // uses FULL DOT-PATHS, so a rogue leaf that collides across sections
  // still shows up here. Dev-mode console.warn names the missing path.
  const walk = (obj) => {
    const parts = path.split(".");
    let cur = obj;
    for (const p of parts) {
      if (cur && typeof cur === "object" && p in cur) cur = cur[p];
      else return undefined;
    }
    return cur;
  };
  const v = walk(dict);
  if (typeof v === "string") return nameCodes(v, dict === translations.ar ? "ar" : "en");   // Round 27 · X6
  const en = walk(translations.en);
  if (typeof en === "string") {
    // Warn only in dev, only when we actually fell back (i.e. dict is
    // NOT the EN dict itself — otherwise every legitimately-missing
    // English key would double-log).
    const isDev = typeof process === "undefined"
      || (process.env && process.env.NODE_ENV !== "production");
    if (isDev && dict !== translations.en
        && typeof console !== "undefined" && console.warn) {
      // eslint-disable-next-line no-console
      console.warn(`[i18n] missing translation for "${path}" — falling back to EN`);
    }
    return nameCodes(en, "en");
  }
  return undefined;  // Y1 fallback
}
